/**
 * Achievements, badges and the 12 week drip (docs/plans/010-achievements.md, docs/SPEC.md section 5). Pure: every condition reads only the
 * save. During an account's first weeks an achievement may also give a cosmetic item (`granted`); afterwards only the badge is recorded
 * and the item is for the shop.
 */
import { MAX_SCORE } from "./score";
import type { BookEntry, SaveGame } from "./storage";
import { weeksBetween } from "./week";

export type RewardLook = "companion" | "decoration" | "shellColour";
export type Reward = { kind: "badge" } | { kind: "item"; look: RewardLook; index: number };

export interface Achievement {
  id: string;
  name: string;
  text: string;
  reward: Reward;
}

export interface EarnedAchievement {
  id: string;
  /** The week it was earned. */
  weekId: string;
  /** True when the item reward was given (earned inside the drip window). */
  granted: boolean;
}

export const DRIP_WEEKS = 12;

const badge: Reward = { kind: "badge" };

/** Fixed order: the Badges sheet shows them in this order. The three item rewards are the "earn it" entries of apps/web/src/game/catalogue.ts. */
export const ACHIEVEMENTS: readonly Achievement[] = [
  { id: "first-hatch", name: "First hatch", text: "Hatch your first pet.", reward: badge },
  { id: "first-week", name: "First week", text: "Get a pet through a whole week.", reward: badge },
  { id: "three-weeks", name: "Three weeks", text: "Have 3 pets in your picture book.", reward: badge },
  { id: "six-weeks", name: "Six weeks", text: "Have 6 pets in your picture book.", reward: { kind: "item", look: "companion", index: 2 } },
  { id: "ten-weeks", name: "Ten weeks", text: "Have 10 pets in your picture book.", reward: badge },
  { id: "good-week", name: "Good week", text: "Finish a week with 600 points or more.", reward: { kind: "item", look: "decoration", index: 2 } },
  { id: "great-week", name: "Great week", text: "Finish a week with 800 points or more.", reward: { kind: "item", look: "shellColour", index: 5 } },
  { id: "near-perfect", name: "Nearly perfect", text: "Finish a week with 950 points or more.", reward: badge },
  { id: "full-score", name: "Full score", text: `Finish a week with all ${MAX_SCORE} points.`, reward: badge },
  { id: "twice-great", name: "Twice great", text: "Finish two weeks with 800 points or more.", reward: badge },
  { id: "dressed-tank", name: "Dressed tank", text: "Put a toy, a companion and a decoration in the tank, at least one of them a new one.", reward: badge },
  { id: "own-style", name: "Own style", text: "Pick a shell colour and a pattern that are not the first ones.", reward: badge },
];

type Basis = Pick<SaveGame, "pet" | "book" | "looks" | "lastHatchedWeekId">;

const WEEK = /^\d{4}-W\d{2}$/;

/** The earliest week the player is known to have played in, or null if no pet ever hatched. */
export function firstWeek(save: Pick<SaveGame, "pet" | "book" | "lastHatchedWeekId">): string | null {
  const weeks = [...save.book.map((b) => b.weekId), save.pet?.weekId, save.lastHatchedWeekId].filter((w): w is string => typeof w === "string" && WEEK.test(w));
  return weeks.sort()[0] ?? null;
}

/** True while the account is inside its drip window. A save that has not started yet counts as inside. */
export function dripActive(startedWeekId: string | null, currentWeekId: string, windowWeeks: number): boolean {
  if (startedWeekId === null) return true;
  return weeksBetween(startedWeekId, currentWeekId) < windowWeeks;
}

const nth = (book: readonly BookEntry[], n: number): BookEntry | undefined => book[n - 1];
const firstWith = (book: readonly BookEntry[], points: number): BookEntry | undefined => book.find((b) => b.points >= points);
const nthWith = (book: readonly BookEntry[], points: number, n: number): BookEntry | undefined => book.filter((b) => b.points >= points)[n - 1];

/**
 * The week an achievement's condition first holds, or null while it does not. `now` is the week to use when the save cannot say (looks).
 */
function weekOf(id: string, s: Basis, now: string): string | null {
  switch (id) {
    case "first-hatch":
      return firstWeek(s);
    case "first-week":
      return nth(s.book, 1)?.weekId ?? null;
    case "three-weeks":
      return nth(s.book, 3)?.weekId ?? null;
    case "six-weeks":
      return nth(s.book, 6)?.weekId ?? null;
    case "ten-weeks":
      return nth(s.book, 10)?.weekId ?? null;
    case "good-week":
      return firstWith(s.book, 600)?.weekId ?? null;
    case "great-week":
      return firstWith(s.book, 800)?.weekId ?? null;
    case "near-perfect":
      return firstWith(s.book, 950)?.weekId ?? null;
    case "full-score":
      return firstWith(s.book, MAX_SCORE)?.weekId ?? null;
    case "twice-great":
      return nthWith(s.book, 800, 2)?.weekId ?? null;
    case "dressed-tank": {
      const l = s.looks;
      const placed = l.toy !== -1 && l.companion !== -1 && l.decoration !== -1;
      return placed && (l.toy > 0 || l.companion > 0 || l.decoration > 0) ? now : null;
    }
    case "own-style":
      return s.looks.shellColour > 0 && s.looks.shellPattern > 0 ? now : null;
    default:
      return null;
  }
}

export interface AchievementResult {
  save: SaveGame;
  /** What was earned by this check, in list order. */
  added: (Achievement & { granted: boolean })[];
}

/**
 * Records every achievement whose condition now holds and is not recorded yet, and starts the window at the first hatch. Idempotent:
 * checking again changes nothing. `currentWeekId` decides whether the drip window is open for new rewards.
 */
export function checkAchievements(save: SaveGame, currentWeekId: string, windowWeeks: number): AchievementResult {
  const started = save.startedWeekId ?? firstWeek(save);
  const open = dripActive(started, currentWeekId, windowWeeks);
  const have = new Set(save.achievements.map((a) => a.id));
  const earned = [...save.achievements];
  const added: AchievementResult["added"] = [];
  for (const a of ACHIEVEMENTS) {
    if (have.has(a.id)) continue;
    const weekId = weekOf(a.id, save, currentWeekId);
    if (weekId === null) continue;
    const granted = a.reward.kind === "item" && open;
    earned.push({ id: a.id, weekId, granted });
    added.push({ ...a, granted });
  }
  if (added.length === 0 && started === save.startedWeekId) return { save, added };
  return { save: { ...save, startedWeekId: started, achievements: earned }, added };
}

/** Grandfathering for saves from before achievements existed: everything already earned counts, with its reward, and nothing is announced. */
export function grandfather(save: SaveGame): SaveGame {
  const started = save.startedWeekId ?? firstWeek(save);
  const earned: EarnedAchievement[] = [];
  for (const a of ACHIEVEMENTS) {
    const weekId = weekOf(a.id, save, started ?? "");
    if (weekId !== null && weekId !== "") earned.push({ id: a.id, weekId, granted: a.reward.kind === "item" });
  }
  return { ...save, startedWeekId: started, achievements: earned };
}

export const itemKey = (look: string, index: number) => `${look}:${index}`;

/** The cosmetic items this save owns through achievements, as "look:index". */
export function grantedItems(save: Pick<SaveGame, "achievements">): string[] {
  const out: string[] = [];
  for (const e of save.achievements) {
    const a = ACHIEVEMENTS.find((x) => x.id === e.id);
    if (e.granted && a?.reward.kind === "item") out.push(itemKey(a.reward.look, a.reward.index));
  }
  return out.sort();
}

export function isItemGranted(save: Pick<SaveGame, "achievements">, look: string, index: number): boolean {
  return grantedItems(save).includes(itemKey(look, index));
}
